import firebase from '../../firebase/compat'
import { db } from '../../firebase/config'

/**
 * Boekt één inlog- of organisatiewisseling: de teller op de gebruiker en het
 * moment op de organisatie.
 *
 * Schrijft rechtstreeks naar Firestore en niet via de `updateLoginCounter`
 * Cloud Function. Die draait op generatie 1 met 256 MB en laadt bij een koude
 * start de hele `functions/index.js` — in de productielogs gemeten op 3,4 tot
 * 6,3 seconden, tegenover ~100 ms voor deze twee schrijfacties. Dat verschil
 * zit vóór de doorverwijzing, dus de gebruiker staat erop te wachten. Zie
 * docs/inbox.md Bug #221.
 *
 * Niet fire-and-forget maken: het inlogscherm doet daarna een
 * `window.location.reload()`, en dat breekt een nog lopende schrijfactie af.
 * Juist omdát dit snel is, kan de aanroeper hem gewoon afwachten.
 *
 * De rechten staan dit toe zonder tussenkomst van de server: `Users` staat een
 * update toe op je eigen document (`request.auth.uid == id`, en `SuperAdmin` en
 * `Compagny` blijven daar apart bevroren), en `CompagnyMeta` toetst met
 * `isTeamMember()` op de `CompagnyID` van het document zélf — niet op de
 * organisatie in de URL. Vandaar dat dit ook werkt vanaf `/login`, waar er nog
 * geen organisatie in de URL staat.
 *
 * Best-effort: een mislukte teller mag een inlog of een wisseling nooit
 * blokkeren. De fout gaat naar de console en de aanroeper krijgt `false`.
 *
 * `LastLogin` gebruikt serverTimestamp() en niet de `timestamp` uit
 * firebase/config: die staat vast op het moment dat de pagina werd geladen.
 * AI-CONTEXT: See docs/pitfalls.md#state-stale-module-level-timestamp
 *
 * @param {object} params
 * @param {string} params.userDocId - Docid van het Users-document (gelijk aan de auth-uid)
 * @param {string} [params.compagnyId] - CompagnyID van de organisatie waar naartoe wordt gegaan; zonder deze wordt alleen de gebruiker geteld
 * @returns {Promise<boolean>} of beide schrijfacties geslaagd zijn
 */
const recordLogin = async ({ userDocId, compagnyId }) => {
    if (!userDocId) return false

    try {
        // Eén tijdstip voor beide regels, anders lopen de gebruiker en de
        // organisatie milliseconden uit elkaar voor hetzelfde moment.
        const now = firebase.firestore.FieldValue.serverTimestamp()

        const userUpdate = db.collection('Users').doc(userDocId).update({
            Logins: firebase.firestore.FieldValue.increment(1),
            LastLogin: now,
        })

        // Zonder organisatie valt er niets te stempelen. Dat is geen fout: bij
        // een account zonder organisatie komt de aanroeper hier niet eens.
        if (!compagnyId) {
            await userUpdate
            return true
        }

        const metaUpdate = db.collection('CompagnyMeta')
            .where('CompagnyID', '==', compagnyId)
            .limit(1)
            .get()
            .then(snap => snap.empty ? null : snap.docs[0].ref.update({ LastLogin: now }))

        await Promise.all([userUpdate, metaUpdate])

        return true
    } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Bijwerken van de inlogteller mislukt:', err)
        return false
    }
}

export default recordLogin
